import { securedWrap } from '@mop/shared/utils/securedWrap';
import { isClient } from '@mop/shared/utils/util';
import { ssrCache } from '@/api/utils/ssrCache';

import type {
  CmsStoryModelRef,
  CmsStoryParams,
  CmsStoriesParams,
  CmsStoryListModelRef,
  CmsStoryResponseData,
  CmsStoryListModel,
  CmsStoryConfigName,
} from '@/types/cms';
import { cmsStoryModel, cmsStoryListModel } from '@/models';

type LoadingState = {
  getStory: boolean;
  getStories: boolean;
  getCmsStoryByConfigId: boolean;
  loading: boolean;
};

const COMPANY_SLUG_PREFIX = constants.STORYBLOK.COMPANY_SLUG_PREFIX;

export function useMopCms(ssrCacheId?: string) {
  const nuxtApp = useNuxtApp();
  const { $gtm, $apiCms } = nuxtApp;
  const isStoryblokLivePreview = nuxtApp.$storyblokLivePreview?.isEnabled ?? false;

  const CACHE_STORY = `story-${ssrCacheId}`;
  const memoryCache = ssrCache();
  const storyResponseRef: Ref<CmsStoryResponseData | null> = useMopSSR<CmsStoryResponseData | null>(CACHE_STORY, null);
  // shallowRef: both hold read-only fetched data replaced wholesale, never mutated in place.
  const cmsStoryModelRef: CmsStoryModelRef | Ref<null> = shallowRef(cmsStoryModel(null));
  const cmsStoryListModelRef: CmsStoryListModelRef | Ref<null> = shallowRef(cmsStoryListModel(storyResponseRef.value));
  const loadingRef: Ref<LoadingState> = ref({
    getStory: false,
    getStories: false,
    getCmsStoryByConfigId: false,
    get loading() {
      return isLoading(loadingRef);
    },
  });

  async function getCmsStory(slug: string, params: CmsStoryParams = {}, siteLocale?: string) {
    if (cmsStoryModelRef.value?.isInitialized()) {
      return;
    }

    if (!ssrCacheId) {
      storyResponseRef.value = null;
    }

    loadingRef.value.getStory = true;

    if (isClient || isStoryblokLivePreview) {
      storyResponseRef.value ??= await $apiCms.getStory(`${COMPANY_SLUG_PREFIX}${slug}`, {
        resolve_relations: 'Card.linkPreview',
        ...params,
      });
    } else {
      const cacheKey = `${CACHE_STORY}-${slug}-${nuxtApp.$mopI18n.locale}-${params?.language}-${params?.fallback_lang}`;
      storyResponseRef.value = await memoryCache.get(cacheKey, () => {
        return $apiCms.getStory(`${COMPANY_SLUG_PREFIX}${slug}`, {
          resolve_relations: 'Card.linkPreview',
          ...params,
        });
      });
    }

    cmsStoryModelRef.value = cmsStoryModel(storyResponseRef.value, siteLocale);
    loadingRef.value.getStory = false;
  }

  async function getCmsStoryByUuid(uuid: string, params: CmsStoryParams = {}) {
    loadingRef.value.getStory = true;
    params.find_by = 'uuid';
    cmsStoryModelRef.value = cmsStoryModel(await $apiCms.getStory(uuid, params));
    loadingRef.value.getStory = false;
  }

  async function getCmsStoryByConfigId(
    configName: CmsStoryConfigName,
    configValue: unknown,
    cmsParams: CmsStoriesParams = {},
  ) {
    loadingRef.value.getCmsStoryByConfigId = true;
    const params: CmsStoriesParams = {
      filter_query: {
        [configName]: {
          in: configValue,
        },
      },
      ...cmsParams,
    };
    const storyList: CmsStoryListModel = cmsStoryListModel(await $apiCms.getStories(undefined, params));

    if (!storyList.isInitialized() || storyList.hasError()) {
      loadingRef.value.getCmsStoryByConfigId = false;
      return;
    }
    const response: CmsStoryResponseData = storyList.getStoryModelList()?.[0]?.getResponse() || {
      error: {
        code: constants.ERROR_CODE.NOT_FOUND,
      },
    };
    cmsStoryModelRef.value = cmsStoryModel(response);
    loadingRef.value.getCmsStoryByConfigId = false;
  }

  async function getCmsStoryList(slug?: string, params: CmsStoriesParams = {}) {
    loadingRef.value.getStories = true;
    cmsStoryListModelRef.value = cmsStoryListModel(await $apiCms.getStories(`${COMPANY_SLUG_PREFIX}${slug}`, params));
    loadingRef.value.getStories = false;
  }

  function trackPageView(pageName: string) {
    if (cmsStoryModelRef.value?.hasErrorNotFound()) {
      throw createError({
        statusCode: 404,
        statusMessage: 'PAGE_NOT_FOUND',
      });
    } else {
      $gtm.trackPageView({
        pageType: pageName,
      });
    }
  }

  return securedWrap({
    getCmsStory,
    getCmsStoryByUuid,
    getCmsStoryList,
    getCmsStoryByConfigId,
    trackPageView,
    cmsStoryModelRef,
    cmsStoryListModelRef,
    loadingRef,
  });
}
